import { joiResolver } from "@hookform/resolvers/joi";
import { ProductType } from "@happy-legal/graphql-enums";
import { Discount } from "@happy-legal/graphql-types";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
} from "@mui/material";
import Joi from "joi";
import { cloneDeep } from "lodash";
import { ChangeEvent, useContext, useEffect } from "react";
import { FormProvider, get, useForm } from "react-hook-form";
import HLSelect from "../../../shared/components/Form/HLSelect";
import HLTextField from "../../../shared/components/Form/HLTextField";
import { DiscountTypeEnum, ProductTypeEnum } from "../../../shared/enums";
import { PartitionKeyEnum as PartitionKeyEnumLocal } from "../../../shared/enums";
import { hasDirtyFields } from "../../../shared/helpers/hasDirtyFields";
import { DiscountContext } from "../../../shared/providers/DiscountProvider";
import { ADD_DISCOUNT, UPDATE_DISCOUNT } from "../../../shared/reducer";

interface IDiscountCodesDialog {
  id: string;
  handleCloseDialog: () => void;
}

// Default values for discount codes
const defaultValues = {
  allowedUses: -1,
  amount: 0,
  discountType: undefined,
  expire: "",
  products: undefined,
  productTypes: undefined,
  text: "",
};

/**
 * Form dialog for creation/update of partner data
 * @param id of discount code being edited
 * @param handleCloseDialog fn for closing dialog
 */
const DiscountCodesDialog = ({
  id,
  handleCloseDialog,
}: IDiscountCodesDialog) => {
  // Load state and reducer dispatch
  const { state, dispatch } = useContext(DiscountContext);

  // Remove currently edited id from validation that prevents reusing ids
  const prevDiscountCodes =
    state
      ?.filter((discount) => discount.id !== id)
      .map((discount) => discount.id) || [];

  // Initialise RHF and assign state as default values when loaded
  const methods = useForm<Partial<Discount>>({
    mode: "all",
    defaultValues:
      cloneDeep(state)?.find((discount) => discount.id === id) || defaultValues,
    // allowedUses gets hidden on checkbox, retain values
    shouldUnregister: false,
    resolver: joiResolver(
      Joi.object({
        id: Joi.string()
          // invalidate discount codes already in use
          .invalid(...prevDiscountCodes)
          .messages({
            "any.invalid": "ID muss eindeutig sein",
            "string.empty": "ID ist erforderlich",
          }),
        amount: Joi.number()
          .min(1)
          .when("/discountType", {
            is: "PERCENTAGE",
            then: Joi.number().min(1).max(100),
          })
          .messages({
            "number.base": "Betrag muss eine Zahl sein",
            "number.min": "Betrag muss mehr als 1",
            "number.max": "Rabatt darf nicht mehr als 100% betragen",
          }),
        products: Joi.array().min(1).required().messages({
          "array.min": "Mindestens eine muss ausgewählt werden",
          "any.required": "Mindestens eine muss ausgewählt werden",
        }),
        productTypes: Joi.array().min(1).required().messages({
          "array.min": "Mindestens eine muss ausgewählt werden",
          "any.required": "Mindestens eine muss ausgewählt werden",
        }),
      }).options({ stripUnknown: true })
    ),
  });
  const {
    register,
    unregister,
    watch,
    getValues,
    setValue,
    formState: { errors, isSubmitSuccessful },
  } = methods;

  // Register fields that are not displayed, but required
  useEffect(() => {
    void register("products");
    void register("productTypes");
    return () => {
      void unregister("products");
      void unregister("productTypes");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dependant form values
  const products = watch("products") as string[];
  const productTypes = watch("productTypes") as string[];
  const discountType = watch("discountType");
  const allowedUses = watch("allowedUses");
  const isUnlimitedUses = allowedUses === -1;

  // Errors
  const productsError = get(errors, "products");
  const productTypesError = get(errors, "productTypes");

  // Fns to manage product/productType checkbox groups
  const handleProductCheckboxes = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    // Get the current array values in field names, with empty array if undefined
    let newProducts: string[] = Array.isArray(products) ? products : [];

    //Remove or add to field array if (un-)checked
    if (newProducts.includes(name) && !checked) {
      newProducts = newProducts.filter(
        (oldProduct: string) => oldProduct !== name
      );
    } else {
      newProducts = [...newProducts, name];
    }

    // Cast to expected type - string[] will be mapped to PartitionKeyEnum later
    setValue("products", newProducts as any, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleProductTypeCheckboxes = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    // Get the current array values in field names, with empty array if undefined
    let newProductTypes: string[] = Array.isArray(productTypes) ? productTypes : [];

    //Remove or add to field array if (un-)checked
    if (newProductTypes.includes(name) && !checked) {
      newProductTypes = newProductTypes.filter(
        (oldProduct: string) => oldProduct !== name
      );
    } else {
      newProductTypes = [...newProductTypes, name];
    }

    // Cast to expected type - string[] will be mapped to ProductType later
    setValue("productTypes", newProductTypes as any, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // Set field to unlimited (-1) if checked, else 0 allowed uses
  const handleAllowedUsesCheckbox = (event: ChangeEvent<HTMLInputElement>) =>
    setValue("allowedUses", event.target.checked ? -1 : 0, {
      shouldDirty: true,
      shouldValidate: true,
    });

  // Submission fn, parsing integers before forming and calling the dispatcher
  const handleSubmit = async () => {
    // shouldUnregister parameter does not return all formDat in submission
    const payload = getValues() as any;

    if (id) {
      dispatch({
        type: UPDATE_DISCOUNT,
        payload: payload as any,
      });
    } else {
      dispatch({ type: ADD_DISCOUNT, payload: payload as any });
    }
  };

  // Bypass memory leak cause by not consitently mounting dialog
  useEffect(() => {
    if (!isSubmitSuccessful) return;
    handleCloseDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Dialog open onClose={handleCloseDialog}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogContent>
            <HLTextField
              label="ID"
              name="id"
              disabled={!!id}
              margin="normal"
              // Make all codes uppercase
              onChange={(e) => {
                setValue("id", e.target.value.toUpperCase(), {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              required
            />
            <HLTextField
              label="Betrag"
              name="amount"
              type="number"
              margin="normal"
              {...(!!discountType && {
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {discountType === "PERCENTAGE" ? "%" : "€"}
                    </InputAdornment>
                  ),
                },
              })}
              required
            />
            <HLSelect
              label="Rabatttyp"
              name="discountType"
              // Invert key value for select menu item mapping
              options={Object.entries(DiscountTypeEnum).map(
                ([value, label]) => ({ label, value })
              )}
              margin="normal"
              required
            />
            <FormControl margin="normal">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isUnlimitedUses}
                    onChange={handleAllowedUsesCheckbox}
                  />
                }
                label="Unbegrenzte Nutzung"
              />
            </FormControl>
            {!isUnlimitedUses && (
              <HLTextField
                label="erlaubte Nutzungen"
                name="allowedUses"
                type="number"
                margin="normal"
                required
              />
            )}
            <HLTextField
              label="Ablaufdatum"
              name="expire"
              type="date"
              margin="normal"
              required
            />
            <Grid container spacing={3}>
              <Grid size={{ xs: 6 }}>
                <FormControl component="fieldset" required margin="normal">
                  <FormLabel component="legend">Produkte</FormLabel>
                  <FormGroup>
                    {Object.entries(PartitionKeyEnumLocal).map(
                      // Invert key value for checkox mapping
                      ([partitionKey, label]) => (
                        <FormControlLabel
                          key={partitionKey}
                          control={
                            <Checkbox
                              checked={products?.includes(partitionKey as string)}
                              onChange={handleProductCheckboxes}
                              name={partitionKey as string}
                            />
                          }
                          label={label}
                        />
                      )
                    )}
                  </FormGroup>
                  {productsError && (
                    <FormHelperText error>
                      {productsError.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl component="fieldset" required margin="normal">
                  <FormLabel component="legend">Produkttypen</FormLabel>
                  <FormGroup>
                    {Object.entries(ProductTypeEnum).map(
                      // Invert key value for checkox mapping
                      ([productType, label]) => (
                        <FormControlLabel
                          key={productType}
                          control={
                            <Checkbox
                              checked={productTypes?.includes(
                                productType as ProductType
                              )}
                              onChange={handleProductTypeCheckboxes}
                              name={productType}
                            />
                          }
                          label={label}
                        />
                      )
                    )}
                  </FormGroup>
                  {productTypesError && (
                    <FormHelperText error>
                      {productTypesError.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
            <HLTextField
              label="Beschreibung"
              name="text"
              multiline
              rows={4}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Abbrechen
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!hasDirtyFields(methods)}
            >
              Speichern
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default DiscountCodesDialog;
